
import React, { useState, useEffect } from "react";
import styles from "./Forms.module.css";
import { Controller, useForm, useWatch } from "react-hook-form";
import TextInput from "../UILibrary/TextInput";
import Submission from "../UILibrary/Submission";
import { Email } from "Utils/Global/regularExpressions";
import { defaultContactValues } from "Utils/Global/globalConstants";
import { ContactService } from "../../../Logic/ContactService";
import { IContactFormData } from "@model/MainPages.model";

const CallToActionForm: React.FC = () => {
  const { control, handleSubmit, reset, formState } = useForm<IContactFormData>(
    {
      mode: "onSubmit",
      defaultValues: {
        ...defaultContactValues,
      },
    }
  );

  const [apiError, setApiError] = useState<string | undefined>(undefined);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const messageValue = useWatch({
    control,
    name: "message",
  });

  useEffect(() => {
    success !== null && formState.isDirty && setSuccess(null);
  }, [formState]);

  const service = new ContactService();

  const onValidSubmit = async (form: IContactFormData) => {
    setSuccess(null);
    const msg = await service.handleContactSubmit({
      data: {...form,
        subject: "Call To Action"},
      reset,
      setApiError: (m) => setApiError(m ?? undefined),
      setLoading: setSubmitting,
    });
    if (msg) setSuccess(msg);
  };

  return (
    <div className={styles.pageWrapper} data-testid="contact-page">
      <div className={`${styles.contactContainer} ${styles.callToActionForm}`} data-testid="contact-container">
        <form data-testid="contact-form">

          {success && (
            <div className={styles.successBanner} data-testid="contact-success">
              {success}
            </div>
          )}

          <div
            className={styles.contactNameFields}
            data-testid="contact-name-fields"
          >
            <Controller
              name="firstName"
              control={control}
              rules={{ required: "First name is required" }}
              render={({ field, fieldState }) => (
                <div data-testid="contact-first-name">
                  <TextInput
                    label="First Name"
                    placeholder="Enter first name"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                  />
                </div>
              )}
            />

            <Controller
              name="lastName"
              control={control}
              rules={{ required: "Last name is required" }}
              render={({ field, fieldState }) => (
                <div data-testid="contact-last-name">
                  <TextInput
                    label="Last Name"
                    placeholder="Enter last name"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                  />
                </div>
              )}
            />
          </div>

          <Controller
            name="email"
            control={control}
            rules={{
              required: { value: true, message: "Email is required!" },
              pattern: { value: Email, message: "Invalid email pattern!" },
            }}
            render={({ field, fieldState }) => (
              <div data-testid="contact-email">
                <TextInput
                  label="Email"
                  placeholder="Enter your email"
                  type="email"
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                />
              </div>
            )}
          />

          <Controller
            name="message"
            control={control}
            rules={{ required: "Message is required" }}
            render={({ field, fieldState }) => (
              <div
                className={styles.contactFormGroup}
                data-testid="contact-message"
              >
                <label
                  className={styles.contactInputLabel}
                  data-testid="contact-message-label"
                >
                  Message
                </label>
                <textarea
                  className={styles.contactTextarea}
                  placeholder="Write your message..."
                  rows={5}
                  value={field.value}
                  onChange={field.onChange}
                  data-testid="contact-message-field"
                />
                <span
                  className={styles.messageChararactersCount}
                  data-testid="message-character-count"
                >
                  {messageValue.length}
                </span>
                {fieldState.error?.message && (
                  <span
                    className={styles.formErrorText}
                    data-testid="contact-message-error"
                  >
                    {fieldState.error.message}
                  </span>
                )}
              </div>
            )}
          />

          <div data-testid="contact-submit">
            <Submission
              error={apiError}
              disabled={submitting}
              buttonText={"Submit"}
              onClick={handleSubmit(onValidSubmit)}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default CallToActionForm;
